import { ApolloError } from '@apollo/client';
import { filesize } from 'filesize';
import moment from 'moment';
import { PlanEntitlementKey } from '../api/graphql/generated';
import i18n, { i18nKeyType } from '../i18n';

interface PlanEntitlementError {
  key: PlanEntitlementKey;
  entitlementValue?: string;
  requestedValue?: string;
}

const BeaconErrorMapping: Record<string, i18nKeyType> = {
  registration_code_invalid: 'common:machine_invalid_registration_code',
  too_many_requests: 'common:too_many_requests_error',
  serial_number_already_registered: 'common:serial_number_already_registered',
};

export interface BeaconError {
  name: string;
  message: string;
}

const getPlanEntitlementError = (
  error: ApolloError
): PlanEntitlementError | undefined => {
  const entitlementError = error?.graphQLErrors[0]?.extensions?.exception as
    | PlanEntitlementError
    | undefined;

  if (entitlementError?.key) {
    return entitlementError;
  }
};

const getBeaconErrorMessage = (error: ApolloError): string | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const beaconError: any = error?.graphQLErrors[0]?.extensions?.exception;

  if (beaconError) {
    const i18nKey = BeaconErrorMapping[beaconError.name];

    if (i18nKey) {
      return i18n.t(i18nKey);
    }
  }
};

const isPlanEntitlementError = (error: ApolloError): boolean => {
  return getPlanEntitlementError(error) !== undefined;
};

const getPlanEntitlementErrorMessage = (error: PlanEntitlementError) => {
  if (error.key === PlanEntitlementKey.CHART_MAX_TIMEFRAME) {
    const value = moment
      .duration(error.entitlementValue, 'milliseconds')
      .asDays();
    return i18n.t('plan:chart_max_timeframe_error', {
      value,
    });
  } else if (error.key === PlanEntitlementKey.CHART_ROW_LIMIT) {
    return i18n.t('plan:chart_row_limit_error', {
      rows: error.entitlementValue,
    });
  } else if (error.key === PlanEntitlementKey.ATTACHMENT_MAX_FILE_UPLOAD_SIZE) {
    // Attachment upload size uses bytes as its unit of measure.
    // No conversion is necessary for the filesize library.
    const value = filesize(Number(error.entitlementValue), {
      base: 2,
      standard: 'jedec',
    });

    return i18n.t('plan:attachment_max_file_upload_size', {
      value,
    });
  } else if (
    error.key === PlanEntitlementKey.ATTACHMENT_MAX_TENANT_STORAGE_SIZE
  ) {
    // Tenant storage size uses megabytes (1024 KB) as its unit of measure.
    // Convert it to bytes for the filesize library.
    const bytes = Number(error.entitlementValue) * 1024 * 1024;
    const value = filesize(bytes, {
      base: 2,
      standard: 'jedec',
    });

    return i18n.t('plan:attachment_max_tenant_storage_size', {
      value,
    });
  }

  return i18n.t('common:save_error');
};

const formatErrorMessage = (
  error: ApolloError | boolean | string,
  defaultErrorMesssageKey?: i18nKeyType
) => {
  let message;

  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof ApolloError) {
    const entitlementError = getPlanEntitlementError(error);

    if (entitlementError !== undefined) {
      message = getPlanEntitlementErrorMessage(entitlementError);
    } else {
      message = getBeaconErrorMessage(error);
    }
  }

  return message ?? i18n.t(defaultErrorMesssageKey ?? 'common:save_error');
};

export { formatErrorMessage, isPlanEntitlementError, getPlanEntitlementError };
